"use client";

import { ThumbsDown, ThumbsUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

type VotingButtonsProps = {
  upvotes: number;
  downvotes: number;
  userVote?: "upvote" | "downvote" | null;
  onVote: (type: "upvote" | "downvote") => void;
  isLoading?: boolean;
  disabled?: boolean;
  size?: "sm" | "lg" | "icon";
  variant?: "default" | "compact";
};

export function VotingButtons({
  upvotes,
  downvotes,
  userVote,
  onVote,
  isLoading = false,
  disabled = false,
  size = "sm",
  variant = "default",
}: VotingButtonsProps) {
  // Show skeleton while loading
  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Skeleton className="h-9 w-20" />
        <Skeleton className="h-9 w-20" />
      </div>
    );
  }

  const iconSizeClasses = {
    sm: "h-4 w-4",
    lg: "h-6 w-6",
    icon: "h-4 w-4",
  };

  const iconSize = iconSizeClasses[size];

  return (
    <div className="flex items-center gap-2">
      <Button
        className={`flex items-center gap-2 ${
          userVote === "upvote"
            ? "bg-green-600 text-white hover:bg-green-700"
            : "hover:border-green-200 hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-950"
        }`}
        disabled={disabled}
        onClick={() => onVote("upvote")}
        size={size}
        variant={userVote === "upvote" ? "primary" : "outline"}
      >
        <ThumbsUp className={iconSize} />
        {variant === "default" && <span>{upvotes}</span>}
      </Button>

      <Button
        className={`flex items-center gap-2 ${
          userVote === "downvote"
            ? "bg-red-600 text-white hover:bg-red-700"
            : "hover:border-red-200 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950"
        }`}
        disabled={disabled}
        onClick={() => onVote("downvote")}
        size={size}
        variant={userVote === "downvote" ? "primary" : "outline"}
      >
        <ThumbsDown className={iconSize} />
        {variant === "default" && <span>{downvotes}</span>}
      </Button>
    </div>
  );
}
